import { useId, useMemo, useState } from "react";
import type { BrandRow, DealRow, TalentRow } from "../../../data/queries";
import { talentName } from "../../../data/queries";
import { matchesSearch } from "../../../components/ui";
import { monthLabel } from "../../../lib/format";
import { t } from "../../../lib/sprache";

const STATUS: Record<string, string> = {
  get planning() { return t("Planung", "Planning"); }, get contract() { return t("Vertrag", "Contract"); }, get concept() { return t("Konzept", "Concept"); }, get production() { return t("Produktion", "Production"); }, get content_approval() { return t("Freigabe", "Content approval"); }, get published() { return t("Veröffentlicht", "Published"); },
  reporting: "Reporting", get invoicing() { return t("Abrechnung", "Billing"); }, get done() { return t("Abgeschlossen", "Completed"); }, get blocked() { return t("Blockiert", "Blocked"); }, get cancelled() { return t("Storniert", "Cancelled"); },
};

function addMonths(key: string, n: number): string {
  const [y, m] = key.split("-").map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** „Marke · Titel · Creator · Zeitraum · Status“: eindeutig, auch bei wiederkehrenden Kampagnen gleichen Namens (Befund 21). */
export function dealLabel(d: Pick<DealRow, "title" | "brand_id" | "talent_id" | "start_month" | "months" | "status" | "archived_at">, brands: readonly Pick<BrandRow, "id" | "name">[], talents: readonly Pick<TalentRow, "id" | "display_name" | "first_name" | "last_name">[]): string {
  const brand = brands.find((b) => b.id === d.brand_id)?.name ?? "";
  const creator = talentName(talents.find((t) => t.id === d.talent_id) as TalentRow | undefined);
  const start = d.start_month?.slice(0, 7) ?? null;
  const period = start ? (d.months && d.months > 1 ? t(`${monthLabel(start)} bis ${monthLabel(addMonths(start, d.months - 1))}`, `${monthLabel(start)} to ${monthLabel(addMonths(start, d.months - 1))}`) : monthLabel(start)) : null;
  const status = d.archived_at ? t("Archiviert", "Archived") : STATUS[d.status] ?? d.status;
  return [brand, d.title, creator, period, status].filter(Boolean).join(" · ");
}

/**
 * Durchsuchbare Auswahl einer Kooperation: gefiltert nach Creator und Marke des Vertrags, stornierte und archivierte
 * nur, wenn sie schon gewählt sind oder ausdrücklich gezeigt werden sollen.
 */
export function KooperationAuswahl({ label, value, onChange, deals, brands, talents, talentId, brandId, emptyLabel = t("Ohne Kooperation", "Without a deal"), disabled, showClosed = false }: {
  label: string;
  value: string;
  onChange: (id: string) => void;
  deals: readonly DealRow[];
  brands: readonly BrandRow[];
  talents: readonly TalentRow[];
  talentId?: string | null;
  brandId?: string | null;
  emptyLabel?: string;
  disabled?: boolean;
  showClosed?: boolean;
}) {
  const id = useId();
  const [search, setSearch] = useState("");
  const options = useMemo(() => deals
    .filter((d) => d.id === value || ((showClosed || (!d.archived_at && d.status !== "cancelled"))
      && (!talentId || d.talent_id === talentId) && (!brandId || d.brand_id === brandId)))
    .map((d) => ({ id: d.id, text: dealLabel(d, brands, talents), start: d.start_month ?? "" }))
    .sort((a, b) => b.start.localeCompare(a.start) || a.text.localeCompare(b.text)), [deals, brands, talents, talentId, brandId, value, showClosed]);
  const shown = options.filter((o) => o.id === value || matchesSearch(o.text, search));
  return (
    <div className="stack tk-deal-pick">
      <label htmlFor={`${id}-q`}>
        {label}
        <input id={`${id}-q`} type="search" value={search} onChange={(e) => setSearch(e.target.value)} disabled={disabled}
          placeholder={t("Suchen nach Marke, Titel, Creator oder Monat", "Search by brand, title, creator or month")} aria-describedby={`${id}-n`} />
      </label>
      <select aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
        <option value="">{emptyLabel}</option>
        {shown.map((o) => <option key={o.id} value={o.id}>{o.text}</option>)}
      </select>
      <small id={`${id}-n`}>{shown.length === options.length ? t(`${options.length} passende Kooperationen`, `${options.length} matching deals`) : t(`${shown.length} von ${options.length} Kooperationen`, `${shown.length} of ${options.length} deals`)}{talentId || brandId ? t(" (gefiltert nach Creator und Marke)", " (filtered by creator and brand)") : ""}</small>
    </div>
  );
}
